// modal-host.ts — THE modal host (Styles x Themes program, PR-3b).
//
// One machine where there were SEVEN. The WF-A audit measured the console's
// dialog engine (#456: stacked hosts, Escape/Tab topmost-wins, focus trap,
// focus return, two-edge motion) standing beside six hand-rolled hosts on the
// Helm — each with its own subset of those behaviours and its own bugs:
//
//   helm-bridge/src/client/app.ts        #overlay  (Settings)   trap + Escape, no focus return, no motion machine
//   helm-bridge/src/client/app.ts        #spawnOverlay          Escape + scrim, NO trap, NO focus return
//   helm-bridge/src/client/cardmenu-history.ts                  Escape + scrim, NO trap, NO focus return, NO role on the host
//   helm-bridge/src/client/terminal.ts   ticketpick             Escape on the INPUT only, NO scrim, NO trap
//   helm-bridge/src/client/release-chrome.ts  rc-dialog         scrim + its own leave animation, NO Escape, NO trap
//   helm-bridge/src/client/palette.ts    palette-overlay        Escape via a global key router, NO trap, NO focus return
//
// Six hosts is six places for "Escape does nothing here" to hide, and it did:
// only ONE of them returned focus to the opener. This module is the console
// engine promoted to the shared pi-stack client layer (the tooltip/buttons
// precedent) and generalised along exactly one axis — the CSS VOCABULARY —
// because the two surfaces name their chrome differently (`.hc-modal` /
// `.hc-dialog` on the Bridge home, `.overlay` / `.modal` on the Helm) and
// neither stylesheet is going to be rewritten to please the other.
//
// THE CONTRACT (unchanged from the console engine; helm-console/src/client/
// dialog.ts is now a thin adapter that re-exports these under its own names,
// so dialog.test.mjs still drives the real engine):
//
//   openModal(host, build, opts?)   custom content (forms, pickers, detail)
//   adoptModal(host, opts?)         a PERSISTENT, already-built panel — the
//                                   Helm's Settings and spawn overlays are
//                                   wired once at boot and must survive close
//   noticeModal(host, spec)         single-acknowledgment outcome dialogs
//   stackHostFor(base)              the one stacked sibling overlay per base
//   destroyModal(host)              instant teardown for route unmounts
//   showModalMsg(el, text)          error/refusal text with an entrance
//
// The confirm-destructive DIALOG is deliberately NOT here: it is one half of
// the confirm-destructive PRIMITIVE, which owns both its tiers in one module
// (confirm.ts) so "how does this product ask before it destroys something"
// has exactly one answer. confirm.ts imports this; nothing here imports it.
//
// Semantics, owner-ruled and ported verbatim:
//   - Both edges animate: enter via double-rAF + `open`, exit via `leaving`
//     + transitionend with the 400ms fallback (motion.ts owns the machine;
//     prefers-reduced-motion collapses both edges to an instant swap).
//   - Escape and a scrim click DISMISS (cancel): 0 side effects, focus
//     returned to whatever opened the dialog.
//   - STACKED: only the TOPMOST open host's document keydown handler acts.
//     Two open hosts both acting on Escape/Tab is the exact double-fire that
//     forced the old content-swap browse design; it is closed at the engine.
//   - Focus: moved to [data-autofocus], else the first focusable, else the
//     panel; Tab is trapped inside the panel; the opener regains focus.
//   - role=dialog + aria-modal on the panel; aria-labelledby wired to the
//     panel's first heading.
//
// The DOM here sticks to createElement/replaceChildren/classList so
// dialog.test.mjs can drive the real engine over the repo's stub-document
// harness (no jsdom in the dep fence); everything browser-only is
// feature-guarded.

import {
  motionNext, prefersReducedMotion, MOTION_LEAVE_FALLBACK_MS,
  type MotionPhase,
} from './motion.js';
import { mkIconBtn } from './buttons.js';

/** The one axis this engine is parameterised on: what each surface CALLS its
 *  chrome. Everything else — the phases, the trap, the stack, the focus
 *  contract — is identical by construction. */
export interface ModalVocabulary {
  /** Class on the OVERLAY host (the scrim). `.hc-modal` / `.overlay`. */
  host: string;
  /** Class added to a lazily-created stacked sibling host. */
  stacked: string;
  /** Class on the PANEL (the dialog box). `.hc-dialog` / `.modal`. */
  panel: string;
  /** Class on the panel's title line. */
  title: string;
  /** Class on the panel's body copy. */
  body: string;
  /** Class on the panel's action row. */
  actions: string;
  /** Class applied to an animated message seat (showModalMsg). */
  msgIn: string;
  /** id prefix for generated aria-labelledby headings. */
  titleId: string;
}

/** The Bridge home's vocabulary (helm-console/src/client/styles.css). */
export const HC_VOCAB: ModalVocabulary = {
  host: 'hc-modal', stacked: 'hc-modal-stacked', panel: 'hc-dialog',
  title: 'hc-dialog-title', body: 'hc-dialog-body', actions: 'hc-dialog-actions',
  msgIn: 'hc-msg-in', titleId: 'hc-dlg-title',
};

/** The Helm's vocabulary (helm-bridge/src/client/styles.css). */
export const HELM_VOCAB: ModalVocabulary = {
  host: 'overlay', stacked: 'overlay-stacked', panel: 'modal',
  title: 'modal-title', body: 'modal-body', actions: 'foot',
  msgIn: 'modal-msg-in', titleId: 'helm-dlg-title',
};

export interface ModalOptions {
  /** Extra class(es) for the panel (space-separated). */
  className?: string;
  /** Escape dismisses (default true). */
  escape?: boolean;
  /** Clicking the scrim dismisses (default true). */
  backdrop?: boolean;
  /** Fired once the modal has fully closed (either edge, any path). */
  onClose?: () => void;
  /** Vocabulary override for one call — the release chrome's `.rc-dialog`
   *  panel rides the Helm host with its own panel class. */
  vocab?: Partial<ModalVocabulary>;
}

export interface ModalHandle {
  /** The panel (content root). */
  panel: HTMLElement;
  /** Animated close; DOM and listeners are torn down when the exit ends. */
  close(): void;
}

export interface NoticeSpec {
  /** Optional title line; body-only notices render just the copy + OK. */
  title?: string;
  body: string;
  okLabel?: string;
  onClose?: () => void;
}

/** What the trap considers focusable. Exported so tests and consumers ask
 * the same question the same way. */
export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Tab-trap decision: given the focused index among `count` focusables
 * (-1 = focus is outside the panel), which index must be FORCED, or null to
 * let the browser move naturally inside the trap. */
export function tabTrapTarget(active: number, count: number, shiftKey: boolean): number | null {
  if (count <= 0) return null; // caller refocuses the panel itself
  if (active === -1) return shiftKey ? count - 1 : 0; // pull focus back in
  if (shiftKey && active === 0) return count - 1; // wrap backwards
  if (!shiftKey && active === count - 1) return 0; // wrap forwards
  return null;
}

// ---------------------------------------------------------------------
// The per-host machine adapter
// ---------------------------------------------------------------------

interface HostState {
  phase: MotionPhase;
  panel: HTMLElement | null;
  opts: ModalOptions;
  prevFocus: HTMLElement | null;
  onKey: ((ev: KeyboardEvent) => void) | null;
  onScrim: ((ev: MouseEvent) => void) | null;
  onLeaveEnd: ((ev: TransitionEvent) => void) | null;
  hideTimer: ReturnType<typeof setTimeout> | null;
  /** adoptModal: the panel is the caller's and survives close. */
  keepPanel: boolean;
}

const states = new WeakMap<HTMLElement, HostState>();
let titleSeq = 0;

// ---------------------------------------------------------------------
// The modal STACK. One modal per host stays the law; stacking is a SECOND
// host overlaid above the first. The stack exists to solve exactly the
// hazard that forced the old content-swap design: every open host holds a
// document keydown listener, so two open hosts would BOTH act on
// Escape/Tab. Topmost wins: only the newest open host's handler acts.
// ---------------------------------------------------------------------

const openHosts: HTMLElement[] = [];

function stackTop(): HTMLElement | null {
  return openHosts[openHosts.length - 1] ?? null;
}

/** How many hosts are open right now — the gate and the suites read this
 *  rather than counting scrims in the DOM. */
export function openModalCount(): number {
  return openHosts.length;
}

const stackHosts = new WeakMap<HTMLElement, HTMLElement>();

/** The stacked overlay host ABOVE `base` — lazily created once per base
 * and reused (same engine, own state). Lands AFTER base in the DOM (its
 * parent when it has one, else document.body): both overlays share the
 * surface's modal z-index, and among equal z the LATER sibling paints
 * above, which is exactly this append. The stub harness's parentless
 * hosts still get a working host back. */
export function stackHostFor(base: HTMLElement, vocab: ModalVocabulary = HC_VOCAB): HTMLElement {
  const got = stackHosts.get(base);
  if (got) return got;
  const host = document.createElement('div');
  host.className = `${vocab.host} ${vocab.stacked}`;
  host.hidden = true;
  const parent = (base as { parentElement?: HTMLElement | null }).parentElement
    ?? (base as { parent?: HTMLElement | null }).parent ?? null;
  if (parent?.appendChild) parent.appendChild(host);
  else if (typeof document !== 'undefined'
    && (document as { body?: HTMLElement }).body?.appendChild) {
    (document as unknown as { body: HTMLElement }).body.appendChild(host);
  }
  stackHosts.set(base, host);
  return host;
}

function stateFor(host: HTMLElement): HostState {
  let st = states.get(host);
  if (!st) {
    st = {
      phase: 'closed', panel: null, opts: {}, prevFocus: null,
      onKey: null, onScrim: null, onLeaveEnd: null, hideTimer: null, keepPanel: false,
    };
    states.set(host, st);
  }
  return st;
}

const raf: (cb: () => void) => void =
  typeof requestAnimationFrame === 'function'
    ? (cb) => { requestAnimationFrame(() => cb()); }
    : (cb) => { setTimeout(cb, 16); };

function clearLeaveWatch(host: HTMLElement, st: HostState): void {
  if (st.hideTimer !== null) { clearTimeout(st.hideTimer); st.hideTimer = null; }
  if (st.onLeaveEnd) {
    host.removeEventListener?.('transitionend', st.onLeaveEnd as EventListener);
    st.onLeaveEnd = null;
  }
}

function focusables(panel: HTMLElement): HTMLElement[] {
  if (typeof panel.querySelectorAll !== 'function') return [];
  return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

function focusInitial(panel: HTMLElement): void {
  const preferred = typeof panel.querySelector === 'function'
    ? (panel.querySelector('[data-autofocus]') as HTMLElement | null)
    : null;
  (preferred ?? focusables(panel)[0] ?? panel).focus?.();
}

function dispatch(host: HTMLElement, st: HostState, ev: 'open' | 'close' | 'settle' | 'hide-done'): void {
  const { phase, fx } = motionNext(st.phase, ev, prefersReducedMotion());
  st.phase = phase;
  // A phase change out of 'leaving' (a reopen mid-fade) makes any pending
  // hide watchers stale; drop them so an old timer cannot hide the reopened
  // dialog. Scheduling below re-arms for the CURRENT leave only.
  if (ev === 'open' || ev === 'hide-done') clearLeaveWatch(host, st);
  for (const c of fx.remove) host.classList.remove(c);
  for (const c of fx.add) host.classList.add(c);
  if (fx.hidden !== undefined) host.hidden = fx.hidden;
  if (fx.scheduleSettle) raf(() => raf(() => dispatch(host, st, 'settle')));
  if (fx.scheduleHide) {
    clearLeaveWatch(host, st);
    st.onLeaveEnd = (tev) => {
      if (tev.target === host) dispatch(host, st, 'hide-done');
    };
    host.addEventListener?.('transitionend', st.onLeaveEnd as EventListener);
    st.hideTimer = setTimeout(() => dispatch(host, st, 'hide-done'), MOTION_LEAVE_FALLBACK_MS);
  }
  if (st.phase === 'closed') teardown(host, st);
}

function teardown(host: HTMLElement, st: HostState): void {
  clearLeaveWatch(host, st);
  const at = openHosts.indexOf(host);
  if (at !== -1) openHosts.splice(at, 1);
  if (st.onKey) { document.removeEventListener('keydown', st.onKey); st.onKey = null; }
  if (st.onScrim) { host.removeEventListener?.('click', st.onScrim as EventListener); st.onScrim = null; }
  // An ADOPTED panel is the caller's: it was built and wired once at boot and
  // must be there, still wired, the next time the host opens. Only panels
  // this engine built are swept.
  if (!st.keepPanel) host.replaceChildren?.();
  st.panel = null;
  st.keepPanel = false;
  const back = st.prevFocus;
  st.prevFocus = null;
  const onClose = st.opts.onClose;
  st.opts = {};
  if (back && (back.isConnected ?? true)) back.focus?.();
  onClose?.();
}

/** Arm the shared per-host listeners (Escape/Tab/scrim) for a fresh open. */
function armHost(host: HTMLElement, st: HostState, close: () => void): void {
  st.prevFocus = (typeof document !== 'undefined'
    ? (document.activeElement as HTMLElement | null)
    : null);
  if (!openHosts.includes(host)) openHosts.push(host);
  st.onKey = (ev: KeyboardEvent) => {
    if (!(host.isConnected ?? true)) { destroyModal(host); return; }
    if (st.phase !== 'open' && st.phase !== 'entering') return;
    // Stacked modals: only the TOPMOST open host acts on the document-level
    // keys — the exact double-fire that forced the old content-swap design,
    // closed at the engine.
    if (stackTop() !== host) return;
    if (ev.key === 'Escape' && st.opts.escape !== false) {
      ev.preventDefault?.();
      close();
      return;
    }
    if (ev.key === 'Tab' && st.panel) {
      const items = focusables(st.panel);
      if (items.length === 0) { ev.preventDefault?.(); st.panel.focus?.(); return; }
      const active = typeof document !== 'undefined'
        ? items.indexOf(document.activeElement as HTMLElement) : -1;
      const force = tabTrapTarget(active, items.length, ev.shiftKey === true);
      if (force !== null) { ev.preventDefault?.(); items[force]?.focus?.(); }
    }
  };
  document.addEventListener('keydown', st.onKey);
  st.onScrim = (ev: MouseEvent) => {
    if (ev.target === host && st.opts.backdrop !== false) close();
  };
  host.addEventListener?.('click', st.onScrim as EventListener);
}

function vocabFor(opts: ModalOptions, base: ModalVocabulary): ModalVocabulary {
  return opts.vocab ? { ...base, ...opts.vocab } : base;
}

/** Label the panel by its own heading when it has one. */
function labelPanel(panel: HTMLElement, v: ModalVocabulary): void {
  if (typeof panel.querySelector !== 'function') return;
  if (panel.getAttribute?.('aria-label')) return; // the caller named it already
  const heading = panel.querySelector('h2, h3') as HTMLElement | null;
  if (!heading) return;
  if (!heading.id) heading.id = `${v.titleId}-${++titleSeq}`;
  panel.setAttribute('aria-labelledby', heading.id);
}

/**
 * Open a modal on `host` (the overlay element). Opening while one is already
 * showing swaps the content in place — the overlay stays up (the console's
 * pin-progress flow updates its notice this way), and the previous content's
 * onClose fires since that dialog is gone.
 */
export function openModal(
  host: HTMLElement,
  build: (body: HTMLElement, close: () => void) => void,
  opts: ModalOptions = {},
  base: ModalVocabulary = HC_VOCAB,
): ModalHandle {
  const v = vocabFor(opts, base);
  const st = stateFor(host);
  const close = (): void => dispatch(host, st, 'close');

  if (st.phase === 'closed') armHost(host, st, close);
  else st.opts.onClose?.(); // content swap: the outgoing content's dialog is over
  st.opts = opts;
  st.keepPanel = false;

  const panel = document.createElement('div');
  panel.className = opts.className ? `${v.panel} ${opts.className}` : v.panel;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('tabindex', '-1');
  build(panel, close);
  labelPanel(panel, v);
  host.replaceChildren?.(panel);
  st.panel = panel;
  dispatch(host, st, 'open');
  focusInitial(panel);
  return { panel, close };
}

/**
 * Open a modal whose PANEL already exists inside `host` and is wired once at
 * boot — the Helm's Settings and spawn overlays, whose several hundred lines
 * of event wiring cannot be rebuilt per open. The panel is adopted, not
 * built: it gains the whole contract (motion, Escape, scrim, Tab trap, focus
 * return, the stack) and survives the close untouched.
 *
 * Returns null when the host holds no panel — an adopt with nothing to adopt
 * is a wiring bug and must not silently look like an open dialog.
 */
export function adoptModal(
  host: HTMLElement,
  opts: ModalOptions = {},
  base: ModalVocabulary = HC_VOCAB,
): ModalHandle | null {
  const v = vocabFor(opts, base);
  const st = stateFor(host);
  const panel = (typeof host.querySelector === 'function'
    ? host.querySelector<HTMLElement>(`.${v.panel}`)
    : null) ?? (host.children?.[0] as HTMLElement | undefined) ?? null;
  if (!panel) return null;
  const close = (): void => dispatch(host, st, 'close');
  if (st.phase === 'open' || st.phase === 'entering') return { panel, close };
  if (st.phase === 'closed') armHost(host, st, close);
  st.opts = opts;
  st.keepPanel = true;
  if (!panel.getAttribute?.('role')) panel.setAttribute('role', 'dialog');
  if (!panel.getAttribute?.('aria-modal')) panel.setAttribute('aria-modal', 'true');
  if (!panel.getAttribute?.('tabindex')) panel.setAttribute('tabindex', '-1');
  labelPanel(panel, v);
  st.panel = panel;
  dispatch(host, st, 'open');
  focusInitial(panel);
  return { panel, close };
}

/** Close whatever is open on `host` through the normal animated path. A
 *  no-op on a closed host, so callers never guard. */
export function closeModal(host: HTMLElement): void {
  const st = states.get(host);
  if (!st || st.phase === 'closed') return;
  dispatch(host, st, 'close');
}

/** True while `host` is showing (entering counts — it is on screen). */
export function isModalOpen(host: HTMLElement): boolean {
  const st = states.get(host);
  return !!st && (st.phase === 'open' || st.phase === 'entering');
}

/** A single-acknowledgment notice. Title optional: outcome notices whose
 * whole content is one measured sentence render body-only (the layout
 * spec's title/hierarchy clauses are for CONFIRMATIONS). */
export function noticeModal(
  host: HTMLElement,
  spec: NoticeSpec,
  base: ModalVocabulary = HC_VOCAB,
): ModalHandle {
  const v = base;
  return openModal(host, (body, close) => {
    if (spec.title) {
      const title = document.createElement('h2');
      title.className = v.title;
      title.textContent = spec.title;
      body.appendChild(title);
    }
    const copy = document.createElement('p');
    copy.className = v.body;
    copy.textContent = spec.body;
    body.appendChild(copy);
    const actions = document.createElement('div');
    actions.className = v.actions;
    // Acknowledgment, not creation: a notice's single button dismisses, so
    // it is NEUTRAL (an additive ramp here would spend the one-additive-
    // per-context seat on "OK").
    const okBtn = mkIconBtn({
      glyph: 'check',
      label: spec.okLabel ?? 'OK',
      attrs: { 'data-act': 'ok', 'data-autofocus': '' },
    });
    okBtn.addEventListener('click', close);
    actions.appendChild(okBtn);
    body.appendChild(actions);
  }, { onClose: spec.onClose }, base);
}

/** Set an error/refusal message with an animated entrance — re-fired per
 * message so a second refusal visibly ARRIVES rather than silently editing
 * text in place. Works on any message seat (.hc-dialog-msg, .hc-create-msg,
 * .hc-fork-status …). */
export function showModalMsg(el: HTMLElement, text: string, cls = HC_VOCAB.msgIn): void {
  el.textContent = text;
  if (!el.classList) return;
  el.classList.remove(cls);
  // Force a style flush so removing+adding restarts the animation.
  void (el as { offsetWidth?: number }).offsetWidth;
  el.classList.add(cls);
}

/** Instant teardown for route unmounts: no exit animation, listeners gone,
 * focus NOT restored (the page is leaving). */
export function destroyModal(host: HTMLElement): void {
  const st = states.get(host);
  if (!st || st.phase === 'closed') return;
  st.phase = 'closed';
  st.prevFocus = null; // never yank focus during a route change
  host.classList.remove('open');
  host.classList.remove('leaving');
  host.hidden = true;
  teardown(host, st);
}
